import axios from '@/lib/axios'
import {
	QueryClient,
	useMutation,
	useQuery,
	useQueryClient,
} from '@tanstack/vue-query'
import { ToastInterface, useToast } from 'vue-toastification'

import type { Phone } from '@/interfaces/phone'

interface Props {
	immediate?: boolean
	params?: Params
}

interface Params extends Omit<Partial<Phone>, 'id'> {
	id: string | number
	phoneId?: string | number
	countryCodeId?: string | number
}

type CreateParams = Required<
	Pick<Params, 'id' | 'type' | 'phone' | 'countryCodeId'>
> & {
	extension?: string
}

const getUserPhone = async (params?: Params): Promise<Phone | void> => {
	if (params) {
		const { id, phoneId } = params
		const { data } = await axios.get(`/api/users/${id}/phones/${phoneId}`)

		return data
	}
}

const createUserPhone = async (
	params: CreateParams,
	toast: ToastInterface,
	queryClient: QueryClient
): Promise<Phone | undefined> => {
	try {
		const { id } = params
		const { data } = await axios.post(`/api/users/${id}/phones`, params)

		queryClient.invalidateQueries(['user', id])
		toast.success('Teléfono creado con éxito')

		return data
	} catch (err: any) {
		toast.error(err.response.data.message)
	}
}

const useUserPhone = (props?: Props) => {
	const toast = useToast()
	const queryClient = useQueryClient()
	const { immediate = true, params } = props || {}

	const createMutation = useMutation((params: CreateParams) =>
		createUserPhone(params, toast, queryClient)
	)

	const { data, isLoading } = useQuery(
		['userPhone'],
		() => getUserPhone(params),
		{
			retry: false,
			enabled: immediate,
		}
	)

	return {
		phone: data,
		isLoading,

		createPhoneMutation: createMutation,
		createPhone: createMutation.mutate,
		createPhoneAsync: createMutation.mutateAsync,
	}
}

export default useUserPhone
